import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import helpContent from '@/help/helpContent';
import {
  Play, Shield, CheckCircle, XCircle, Clock, AlertTriangle,
  ChevronRight, RotateCw, Eye, Zap, X as XIcon
} from 'lucide-react';

const STATUS_COLORS = {
  dry_run: 'bg-blue-50 text-blue-700', pending_approval: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700', running: 'bg-blue-50 text-blue-600',
  success: 'bg-emerald-50 text-emerald-700', failed: 'bg-red-50 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500', rejected: 'bg-red-50 text-red-600',
  expired: 'bg-gray-100 text-gray-400',
};
const RISK_COLORS = {
  low: 'bg-emerald-50 text-emerald-700', medium: 'bg-amber-50 text-amber-700',
  high: 'bg-red-50 text-red-700', critical: 'bg-red-100 text-red-800',
};

export default function ActionOrchestrator() {
  const [tools, setTools] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tools');
  const [selectedTool, setSelectedTool] = useState(null);
  const [inputJson, setInputJson] = useState('{}');
  const [env, setEnv] = useState('sandbox');
  const [executing, setExecuting] = useState(false);
  const [detailRun, setDetailRun] = useState(null);
  const [events, setEvents] = useState([]);
  const [limits, setLimits] = useState({});

  const load = useCallback(async () => {
    try {
      const [t, r, l] = await Promise.all([
        api.get('/tenant/ai-tools'), api.get('/tenant/orchestrator/runs?page_size=50'),
        api.get('/tenant/ai/limits'),
      ]);
      setTools((t.data || []).filter(x => x.status !== 'archived'));
      setRuns(r.data || []); setLimits(l.data || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleExecute = async (dryRun = false) => {
    if (!selectedTool) return;
    setExecuting(true);
    try {
      let payload = {};
      try { payload = JSON.parse(inputJson); } catch { toast.error('JSON inválido'); setExecuting(false); return; }
      const { data } = await api.post('/tenant/orchestrator/execute', {
        tool_id: selectedTool.id, input_payload: payload,
        environment: env, dry_run: dryRun,
      });
      if (dryRun) {
        toast.success(`Dry run: ${data.would_execute ? 'ejecutaría' : 'bloqueado'}${data.requires_approval ? ' (requiere aprobación)' : ''}`);
      } else {
        toast.success(`Run creado: ${data.status}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExecuting(false); }
  };

  const handleAction = async (runId, action) => {
    try {
      await api.post(`/tenant/orchestrator/runs/${runId}/${action}`);
      toast.success(`Acción: ${action}`); load();
      if (detailRun?.id === runId) loadDetail(runId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadDetail = async (runId) => {
    try {
      const [r, ev] = await Promise.all([
        api.get(`/tenant/orchestrator/runs/${runId}`),
        api.get(`/tenant/orchestrator/runs/${runId}/events`),
      ]);
      setDetailRun(r.data); setEvents(ev.data || []); setTab('detail');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-5" data-testid="action-orchestrator">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><Zap className="w-5 h-5" /> Action Orchestrator</h2>
          <p className="text-sm text-[#64748B] mt-0.5">{runs.length} ejecución{runs.length !== 1 ? 'es' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant={tab === 'tools' ? 'default' : 'outline'} onClick={() => setTab('tools')} data-testid="tab-tools">Ejecutar</Button>
          <Button size="sm" variant={tab === 'runs' ? 'default' : 'outline'} onClick={() => setTab('runs')} data-testid="tab-runs">Historial</Button>
        </div>
      </div>

      <SectionHelp module="orchestrator" title={helpContent.orchestrator.title} description={helpContent.orchestrator.description} whatItDoesNot={helpContent.orchestrator.whatItDoesNot} />
      <VerticalExampleBox module="orchestrator" />

      {/* Sprint 53 (16/N) — el usuario notó (con razón) que esta pantalla es
          para desarrolladores: requiere armar un JSON a mano. Ya existe una
          vía real y no técnica para que la IA ejecute acciones sola durante
          la conversación — Config IA → Herramientas. Esta pantalla se deja
          para pruebas técnicas manuales, no se le construye un asistente de
          IA para el JSON porque sería invertir esfuerzo en algo que
          Herramientas ya resuelve mejor para el caso de uso real. */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 flex items-start gap-2">
        <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Esta pantalla es para pruebas técnicas manuales (requiere escribir un JSON a mano) — no es la forma en que tu IA usa herramientas en conversaciones reales con clientes.
          {' '}Para eso, activa las herramientas en <strong>Config IA → Herramientas</strong>, ahí la IA las usa sola durante la conversación, sin que nadie tenga que escribir nada técnico.
        </span>
      </div>

      {tab === 'tools' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2" data-testid="tool-selector">
            <h3 className="text-xs font-bold text-[#64748B] uppercase">Seleccionar herramienta</h3>
            {tools.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin herramientas disponibles.</p> :
              tools.map(t => (
                <div key={t.id} onClick={() => { setSelectedTool(t); setInputJson(JSON.stringify(t.input_schema?.properties ? Object.fromEntries(Object.keys(t.input_schema.properties).map(k => [k, ''])) : {}, null, 2)); }}
                  className={`bg-white border rounded-lg p-3 cursor-pointer transition-colors ${selectedTool?.id === t.id ? 'border-[#0F172A] ring-1 ring-[#0F172A]' : 'border-[#E2E8F0] hover:border-[#94A3B8]'}`}
                  data-testid={`tool-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#0F172A] flex-1 truncate">{t.name}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${RISK_COLORS[t.risk_level] || ''}`}>{t.risk_level}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLORS[t.status] || 'bg-gray-100'}`}>{t.status}</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{t.key} · {t.category}</p>
                </div>
              ))
            }
          </div>

          {selectedTool && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4" data-testid="execute-panel">
              <h3 className="text-sm font-bold">{selectedTool.name}</h3>
              <div className="flex gap-2 flex-wrap text-[10px]">
                <span className={`px-1.5 py-0.5 rounded-full ${RISK_COLORS[selectedTool.risk_level] || ''}`}>Risk: {selectedTool.risk_level}</span>
                {selectedTool.approval_mode !== 'none' && <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full">Aprobación: {selectedTool.approval_mode}</span>}
                <span className="bg-gray-100 px-1.5 py-0.5 rounded-full">{selectedTool.category}</span>
              </div>
              <div>
                <label className="text-xs text-[#64748B] mb-1 block">Input Payload (JSON)</label>
                <textarea value={inputJson} onChange={e => setInputJson(e.target.value)}
                  className="w-full text-xs font-mono border rounded-lg px-3 py-2 h-28 resize-none"
                  data-testid="input-payload" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] mb-1 block">Entorno</label>
                <select value={env} onChange={e => setEnv(e.target.value)}
                  className="w-full text-xs border rounded-lg px-3 py-2" data-testid="env-select">
                  <option value="sandbox">Sandbox</option>
                  <option value="production" disabled={!limits.orchestrator_production_enabled}>Producción {!limits.orchestrator_production_enabled ? '(plan)' : ''}</option>
                </select>
              </div>
              {selectedTool.risk_level === 'high' || selectedTool.risk_level === 'critical' ? (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                  Riesgo {selectedTool.risk_level} — requiere aprobación manual antes de ejecutar.
                </div>
              ) : null}
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => handleExecute(true)} disabled={executing} data-testid="dry-run-btn">
                  <Eye className="w-4 h-4 mr-1" /> Dry Run
                </Button>
                <Button size="sm" onClick={() => handleExecute(false)} disabled={executing} data-testid="execute-btn">
                  <Play className="w-4 h-4 mr-1" /> {executing ? 'Ejecutando...' : 'Ejecutar'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'runs' && (
        <div className="space-y-2" data-testid="runs-list">
          {runs.length === 0 ? <p className="text-sm text-[#94A3B8] text-center py-8">Sin ejecuciones registradas.</p> :
            runs.map(r => (
              <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 hover:border-[#94A3B8] transition-colors cursor-pointer"
                onClick={() => loadDetail(r.id)} data-testid={`run-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{r.requested_action_key || r.run_key}</p>
                  <p className="text-xs text-[#94A3B8]">{r.environment} · {r.created_at?.split('T')[0]}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.status] || 'bg-gray-100'}`}>{r.status}</span>
                {r.status === 'pending_approval' && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={e => { e.stopPropagation(); handleAction(r.id, 'approve'); }} data-testid="approve-btn">Aprobar</Button>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-red-600" onClick={e => { e.stopPropagation(); handleAction(r.id, 'reject'); }} data-testid="reject-btn">Rechazar</Button>
                  </div>
                )}
                {r.status === 'approved' && (
                  <Button size="sm" className="h-6 px-2 text-[10px]" onClick={e => { e.stopPropagation(); handleAction(r.id, 'execute-approved'); }} data-testid="execute-approved-btn">Ejecutar aprobado</Button>
                )}
                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
              </div>
            ))
          }
        </div>
      )}

      {tab === 'detail' && detailRun && (
        <div className="space-y-4" data-testid="run-detail">
          <div className="flex items-center gap-2">
            <button onClick={() => { setTab('runs'); setDetailRun(null); }} className="text-xs text-[#64748B] hover:text-[#0F172A]">← Volver</button>
            <h3 className="text-sm font-bold">{detailRun.requested_action_key}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[detailRun.status]}`}>{detailRun.status}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">Entorno:</span> {detailRun.environment}</div>
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">Riesgo:</span> {detailRun.risk_level}</div>
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">Duración:</span> {detailRun.duration_ms ?? '—'}ms</div>
            <div className="bg-white border rounded-lg p-3"><span className="text-[#94A3B8]">Aprobación:</span> {detailRun.approval_status}</div>
          </div>
          {detailRun.error_message && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">{detailRun.error_message}</div>
          )}
          {detailRun.status === 'pending_approval' && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => handleAction(detailRun.id, 'approve')} data-testid="detail-approve-btn"><CheckCircle className="w-4 h-4 mr-1" /> Aprobar</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleAction(detailRun.id, 'reject')} data-testid="detail-reject-btn"><XCircle className="w-4 h-4 mr-1" /> Rechazar</Button>
              <Button size="sm" variant="outline" onClick={() => handleAction(detailRun.id, 'cancel')} data-testid="detail-cancel-btn"><XIcon className="w-4 h-4 mr-1" /> Cancelar</Button>
            </div>
          )}
          {detailRun.status === 'approved' && (
            <Button size="sm" onClick={() => handleAction(detailRun.id, 'execute-approved')} data-testid="detail-exec-approved-btn"><Play className="w-4 h-4 mr-1" /> Ejecutar aprobado</Button>
          )}
          <div className="bg-white border rounded-xl p-4">
            <h4 className="text-xs font-bold mb-2">Eventos ({events.length})</h4>
            {events.length === 0 ? <p className="text-xs text-[#94A3B8]">Sin eventos.</p> :
              events.map(ev => (
                <div key={ev.id} className="flex items-start gap-2 py-1.5 border-b last:border-0">
                  <span className="text-[10px] text-[#94A3B8] w-6">{ev.step_order}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLORS[ev.event_type] || 'bg-gray-100'}`}>{ev.event_type}</span>
                  <span className="text-xs text-[#0F172A] flex-1">{ev.message}</span>
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}
